import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Pencil, CheckCircle, PowerOff, Loader2, Star,
  Phone, Mail, Shield, Award, AlertTriangle,
  BarChart3, ClipboardList, RefreshCw, ExternalLink,
  CheckCircle2, ClipboardCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { CarrierForm, BLANK_FORM, StatusPill, RatingBar } from './CarriersPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLE = {
  BRONZE:   { bg: 'bg-amber-500/10',  text: 'text-amber-600 dark:text-amber-400'  },
  SILVER:   { bg: 'bg-slate-400/10',  text: 'text-slate-500 dark:text-muted-foreground'  },
  GOLD:     { bg: 'bg-yellow-400/10', text: 'text-yellow-600 dark:text-yellow-400' },
  PLATINUM: { bg: 'bg-blue-500/10',   text: 'text-blue-500  dark:text-blue-400'   },
};

function scoreColor(v) {
  if (v >= 80) return 'text-emerald-500';
  if (v >= 60) return 'text-blue-500';
  if (v >= 40) return 'text-amber-500';
  return 'text-red-500';
}

export default function CarrierProfilePage() {
  const { carrierId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [carrier, setCarrier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [ezData, setEzData] = useState(null);
  const [cdRatings, setCdRatings] = useState(null);
  const [cdLoading, setCdLoading] = useState(false);
  const [fmcsaChecks, setFmcsaChecks] = useState(null);
  const [fmcsaLoading, setFmcsaLoading] = useState(false);

  const fetchCarrier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Not found');
      setCarrier(await res.json());
    } catch {
      toast.error('Carrier not found');
      navigate('/admin/carriers');
    } finally {
      setLoading(false);
    }
  }, [carrierId, token, navigate]);

  useEffect(() => { fetchCarrier(); }, [fetchCarrier]);

  useEffect(() => {
    if (!token) return;
    const fetchEz = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/ez-score`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setEzData(await res.json());
      } catch { /* silent */ }
    };
    fetchEz();
  }, [carrierId, token]);

  const fetchCdRatings = useCallback(async () => {
    if (!token) return;
    setCdLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/cd-ratings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCdRatings(await res.json());
    } catch { /* silent */ }
    finally { setCdLoading(false); }
  }, [carrierId, token]);

  useEffect(() => { fetchCdRatings(); }, [fetchCdRatings]);

  const fetchFmcsaChecks = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/fmcsa-checks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setFmcsaChecks(await res.json());
    } catch { /* silent */ }
  }, [carrierId, token]);

  useEffect(() => { fetchFmcsaChecks(); }, [fetchFmcsaChecks]);

  const handleRunFmcsaCheck = async () => {
    setFmcsaLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/fmcsa-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Check failed');
      }
      toast.success('FMCSA check completed');
      fetchFmcsaChecks();
    } catch (err) { toast.error(err.message); }
    finally { setFmcsaLoading(false); }
  };

  const openEdit = () => {
    setForm({
      company_name: carrier.company_name || '',
      contact_name: carrier.contact_name || '',
      phone: carrier.phone || '',
      email: carrier.email || '',
      mc_number: carrier.mc_number || '',
      dot_number: carrier.dot_number || '',
      customer_id: carrier.customer_id || '',
      is_active: carrier.is_active !== false,
      equipment_types: carrier.equipment_types || [],
      soft_straps_supported: carrier.soft_straps_supported || false,
      regions_served: carrier.regions_served || [],
      insurance_limit: carrier.insurance_limit || 0,
    });
    setEditOpen(true);
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
      toast.success('Carrier updated');
      setEditOpen(false);
      fetchCarrier();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleVerify = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/verify`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to verify');
      toast.success('Carrier verified');
      fetchCarrier();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/toggle-active`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to toggle');
      const data = await res.json();
      toast.success(`Carrier ${data.is_active ? 'activated' : 'deactivated'}`);
      fetchCarrier();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading carrier...
      </div>
    );
  }

  if (!carrier) return null;

  const isPending = carrier.is_active !== false && carrier.status !== 'active_verified';
  const onTimeRate = carrier.on_time_rate != null ? `${(carrier.on_time_rate * 100).toFixed(1)}%` : '—';
  const damageRate = carrier.damage_rate != null ? `${(carrier.damage_rate * 100).toFixed(1)}%` : '—';

  const FlagBadge = ({ active, label }) => active
    ? <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400"><AlertTriangle className="h-3 w-3" />{label}</span>
    : <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" />{label}</span>;

  return (
    <>
      <div className="space-y-4 max-w-5xl">

        {/* ── Header card ── */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-start gap-3 mb-2">
            <button
              onClick={() => navigate('/admin/carriers')}
              className="text-muted-foreground hover:text-foreground transition-colors mt-1 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap mb-2">
                <h1 className="font-outfit text-xl font-bold text-foreground">{carrier.company_name}</h1>
                <StatusPill carrier={carrier} />
              </div>

              {/* Contact strip */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-3">
                {carrier.phone && (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {carrier.phone}
                  </span>
                )}
                {carrier.email && (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" /> {carrier.email}
                  </span>
                )}
                {carrier.dot_number && (
                  <span className="text-sm text-muted-foreground">
                    DOT# <span className="font-mono font-medium text-foreground">{carrier.dot_number}</span>
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {isPending && (
                  <Button
                    onClick={handleVerify}
                    size="sm"
                    variant="outline"
                    className="h-8 border-emerald-500/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" /> Verify
                  </Button>
                )}
                <Button
                  onClick={() => navigate(`/admin/carriers/${carrierId}/vetting`)}
                  variant="outline"
                  size="sm"
                  className="h-8 border-blue-500/40 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10"
                >
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> Vetting
                </Button>
                <Button onClick={handleToggle} variant="outline" size="sm" className="h-8">
                  <PowerOff className="h-3.5 w-3.5 mr-1.5" />
                  {carrier.is_active !== false ? 'Deactivate' : 'Activate'}
                </Button>
                <Button onClick={openEdit} size="sm" className="bg-accent text-white hover:bg-accent/90 h-8">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
              </div>

              {/* Internal rating bar */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground shrink-0">Internal rating</span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-500 transition-all"
                    style={{ width: `${carrier.rating_internal ?? 50}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                  {carrier.rating_internal ?? 50} / 100
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats strip ── */}
        <div className="grid grid-cols-6 gap-3">
          {[
            { label: 'Total loads',   value: ezData?.score?.stats?.total_loads ?? carrier.total_deliveries ?? 0 },
            { label: 'Completed',     value: ezData?.score?.stats?.completed_loads ?? 0 },
            { label: 'Cancelled',     value: ezData?.score?.stats?.cancelled_loads ?? 0, red: (ezData?.score?.stats?.cancelled_loads ?? 0) > 0 },
            { label: 'Deliveries',    value: carrier.total_deliveries ?? 0 },
            { label: 'On-time rate',  value: onTimeRate },
            { label: 'Damage rate',   value: damageRate },
          ].map(({ label, value, red }) => (
            <div key={label} className="bg-card border border-border rounded-xl p-3">
              <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
              <p className={`text-xl font-bold tabular-nums ${red ? 'text-red-500' : 'text-foreground'}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Two-column main layout ── */}
        <div className="grid grid-cols-2 gap-4 items-start">

          {/* Left column */}
          <div className="space-y-4">

            {/* Contact & addresses */}
            {(carrier.phone_numbers?.length > 0 || carrier.addresses?.length > 0) && (
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-sm text-foreground">Contact & addresses</span>
                </div>

                {carrier.phone_numbers?.length > 0 && (
                  <div className="space-y-0">
                    {carrier.phone_numbers.map(p => (
                      <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                        <span className="text-xs text-muted-foreground capitalize">{p.type?.replace(/_/g, ' ')} phone</span>
                        <a
                          href={`tel:${p.phone_number}`}
                          className="text-xs font-medium text-foreground hover:text-accent transition-colors"
                        >
                          {p.phone_number}{p.extension ? ` ext. ${p.extension}` : ''}
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {carrier.addresses?.length > 0 && (
                  <div className="pt-1 border-t border-border grid grid-cols-2 gap-4">
                    {carrier.addresses.map(a => (
                      <div key={a.id}>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{a.type}</p>
                        <p className="text-xs text-foreground">{a.street_address}</p>
                        {a.street_address2 && <p className="text-xs text-foreground">{a.street_address2}</p>}
                        <p className="text-xs text-muted-foreground">{[a.city, a.state, a.zipcode].filter(Boolean).join(', ')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Central Dispatch Rating */}
            <div className="bg-card border border-border rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-semibold text-sm text-foreground">Central Dispatch rating</span>
                  {cdRatings && (
                    <a
                      href={`https://app.centraldispatch.com/ratings/ratings?customerid=${cdRatings.customerId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" /> View on CD
                    </a>
                  )}
                </div>
                <button
                  onClick={fetchCdRatings}
                  disabled={cdLoading}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-2.5 py-1 rounded-full transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${cdLoading ? 'animate-spin' : ''}`} />
                  {cdLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {cdLoading && !cdRatings && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {!cdLoading && !cdRatings && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No CD ratings available. Ensure the carrier has a Central Dispatch ID.
                </p>
              )}

              {cdRatings && (() => {
                const avg   = parseFloat(cdRatings.averageRating) || 0;
                const total = cdRatings.totalReviews || 0;
                const starColor = avg >= 4 ? 'text-amber-500' : avg >= 3 ? 'text-amber-400' : 'text-red-500';
                const starDist = [
                  { label: '5★', count: cdRatings.total5Count || 0 },
                  { label: '4★', count: cdRatings.total4Count || 0 },
                  { label: '3★', count: cdRatings.total3Count || 0 },
                  { label: '2★', count: cdRatings.total2Count || 0 },
                  { label: '1★', count: cdRatings.total1Count || 0 },
                ];

                return (
                  <>
                    {/* Rating overview */}
                    <div className="flex items-start gap-4">
                      <div className="shrink-0">
                        <p className={`text-4xl font-bold tabular-nums ${starColor}`}>{avg.toFixed(1)}</p>
                        <div className="flex items-center gap-0.5 my-1">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`h-3.5 w-3.5 ${i <= Math.round(avg) ? 'text-amber-400 fill-amber-400' : 'text-muted'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{total.toLocaleString()} reviews</p>
                      </div>
                      <div className="flex-1 space-y-1">
                        {starDist.map(({ label, count }) => (
                          <div key={label} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-5 shrink-0">{label}</span>
                            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full bg-amber-400 transition-all"
                                style={{ width: total > 0 ? `${(count / total) * 100}%` : '0%' }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground tabular-nums w-6 text-right">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Category ratings */}
                    {cdRatings.ratings?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 text-center">
                        {cdRatings.ratings.map(r => (
                          <div key={r.ratingType} className="bg-muted/40 rounded-lg p-3">
                            <p className="text-lg font-bold tabular-nums text-foreground">{parseFloat(r.ratingValue).toFixed(1)}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{r.ratingType}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Optional questions / feedback */}
                    {cdRatings.optionalQuestions?.length > 0 && (
                      <div className="space-y-2 border-t border-border pt-3">
                        {[...cdRatings.optionalQuestions]
                          .sort((a, b) => a.questionNumber - b.questionNumber)
                          .map(q => {
                            const pct = q.percentage === 'N/A' ? null : parseInt(q.percentage, 10);
                            return (
                              <div key={q.questionNumber} className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                  <span className="text-xs text-muted-foreground truncate">{q.answerDescription}</span>
                                </div>
                                {pct !== null ? (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full bg-emerald-500 transition-all"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <span className={`text-xs font-semibold tabular-nums ${pct >= 90 ? 'text-emerald-500' : pct >= 70 ? 'text-amber-500' : 'text-red-500'}`}>
                                      {pct}%
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground shrink-0">N/A</span>
                                )}
                              </div>
                            );
                          })
                        }
                      </div>
                    )}

                    {carrier.cd_ratings_synced_at && (
                      <p className="text-xs text-muted-foreground text-right border-t border-border pt-2">
                        Last synced {new Date(carrier.cd_ratings_synced_at).toLocaleString()}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Registration & identifiers */}
            <div className="bg-card border border-border rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-sm text-foreground">Registration & identifiers</span>
              </div>
              <div className="space-y-0">
                {/* US DOT with verification badge */}
                {carrier.dot_number && (() => {
                  const dotAuth = carrier.authority_numbers?.find(a =>
                    a.type?.toLowerCase().includes('dot') || a.number === carrier.dot_number
                  );
                  return (
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="text-xs text-muted-foreground">US DOT</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-medium text-foreground">{carrier.dot_number}</span>
                        {dotAuth?.verified
                          ? <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">Verified</span>
                          : <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">Unverified</span>
                        }
                      </div>
                    </div>
                  );
                })()}

                {[
                  ['Equipment types', carrier.equipment_types?.length > 0 ? carrier.equipment_types.join(', ') : '—'],
                  ['Insurance limit', `$${(carrier.insurance_limit || 0).toLocaleString()}`],
                  ['Total reviews',   carrier.total_reviews ?? 0],
                  ['Member since',    carrier.created_at ? new Date(carrier.created_at).toLocaleDateString() : null],
                ].filter(([, v]) => v != null).map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <span className="text-xs font-medium text-foreground">{value}</span>
                  </div>
                ))}

                {[
                  ['Internal ID',           carrier.id],
                  ['Central Dispatch ID',   carrier.customer_id],
                  ['Manheim customer ID',   carrier.manheim_customer_id],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground shrink-0">{label}</span>
                    <span title={value} className="text-xs font-mono text-foreground text-right cursor-default">{String(value).slice(-14)}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right column */}
          <div className="space-y-4">

            {/* FMCSA Validation */}
            {(() => {
              const latest = fmcsaChecks?.[0];
              const pass = latest?.overall_pass;
              return (
                <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <span className="font-semibold text-sm text-foreground">FMCSA validation</span>
                      {latest && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          pass === true  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                          pass === false ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {pass === true ? 'Passed' : pass === false ? 'Flags found' : 'Unknown'}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleRunFmcsaCheck}
                      disabled={fmcsaLoading}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-2.5 py-1 rounded-full transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3 w-3 ${fmcsaLoading ? 'animate-spin' : ''}`} />
                      {fmcsaLoading ? 'Checking...' : 'Re-check'}
                    </button>
                  </div>

                  {fmcsaLoading && !fmcsaChecks && (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {!latest && !fmcsaLoading && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No FMCSA check on file. Click <span className="font-medium text-foreground">Re-check</span> to run one.
                    </p>
                  )}

                  {latest && (
                    <>
                      {/* 2×2 status grid */}
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          {
                            label: 'Authority',
                            value: latest.authority_status || '--',
                            bad: latest.authority_status !== 'ACTIVE',
                          },
                          {
                            label: 'Safety rating',
                            value: latest.safety_rating || 'Not rated',
                            bad: latest.safety_rating === 'Unsatisfactory',
                          },
                          {
                            label: 'Insurance',
                            value: latest.insurance_on_file == null ? '--' : latest.insurance_on_file ? 'On file' : 'Not on file',
                            bad: latest.insurance_on_file === false,
                          },
                          {
                            label: 'Out of service',
                            value: latest.out_of_service == null ? '--' : latest.out_of_service ? 'Yes' : 'No',
                            bad: latest.out_of_service === true,
                          },
                        ].map(({ label, value, bad }) => (
                          <div key={label} className="bg-muted/40 rounded-lg p-2.5">
                            <p className="text-xs text-muted-foreground mb-1">{label}</p>
                            <p className={`text-sm font-semibold ${bad ? 'text-red-500' : 'text-emerald-500'}`}>{value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Flag pills */}
                      <div className="flex flex-wrap gap-1.5">
                        {latest.flag_no_authority != null && <FlagBadge active={latest.flag_no_authority} label="No active authority" />}
                        {latest.flag_safety_risk  != null && <FlagBadge active={latest.flag_safety_risk}  label="Safety risk" />}
                        {latest.flag_no_insurance != null && <FlagBadge active={latest.flag_no_insurance} label="No insurance" />}
                        {latest.flag_oos          != null && <FlagBadge active={latest.flag_oos}          label="Out of service" />}
                      </div>

                      {/* Carrier info rows */}
                      {(latest.legal_name || latest.carrier_operation || latest.driver_count != null || latest.power_unit_count != null) && (
                        <div className="border-t border-border pt-2 space-y-0">
                          {[
                            ['Legal name',   latest.legal_name],
                            ['Operation',    latest.carrier_operation],
                            ['Drivers',      latest.driver_count],
                            ['Power units',  latest.power_unit_count],
                          ].filter(([, v]) => v != null).map(([label, value]) => (
                            <div key={label} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                              <span className="text-xs text-muted-foreground">{label}</span>
                              <span className="text-xs font-medium text-foreground">{value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <p className="text-xs text-muted-foreground border-t border-border pt-2">
                        Checked {new Date(latest.checked_at).toLocaleString()} · Source: {latest.check_source}
                        {fmcsaChecks.length > 1 && ` · ${fmcsaChecks.length} checks on record`}
                      </p>
                    </>
                  )}
                </div>
              );
            })()}

            {/* EZ Score */}
            {ezData?.score && (() => {
              const s = ezData.score;
              const tier = TIER_STYLE[s.tier] || TIER_STYLE.BRONZE;
              return (
                <div className="bg-card border border-border rounded-xl p-4 space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <span className="font-semibold text-sm text-foreground">EZ Score</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tier.bg} ${tier.text}`}>
                        {s.tier}
                      </span>
                    </div>
                    {s.auto_assignable
                      ? <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><CheckCircle className="h-3 w-3" /> Auto-Assignable</span>
                      : <span className="text-xs text-muted-foreground">Manual review required</span>
                    }
                  </div>

                  {/* Score trio */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[
                      { label: 'Overall', value: s.final_score },
                      { label: 'Rule',    value: s.rule_score  },
                      { label: 'AI',      value: s.ai_score    },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-muted/40 rounded-lg p-3">
                        <p className={`text-2xl font-bold tabular-nums ${scoreColor(value)}`}>{value.toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Score Breakdown */}
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Score Breakdown</p>
                    <div className="space-y-0">
                      {Object.entries(s.breakdown).map(([key, val]) => (
                        <div key={key} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                          <span className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className={`text-xs font-semibold tabular-nums ${scoreColor(val)}`}>{val.toFixed(1)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Score Contributions */}
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Score Contributions</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(s.contributions).map(([key, val]) => (
                        <span
                          key={key}
                          className={`px-2 py-1 rounded-md text-xs font-semibold ${
                            val < 0
                              ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                              : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          }`}
                        >
                          {key.replace(/_/g, ' ')} {val >= 0 ? '+' : ''}{val.toFixed(1)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Authority numbers */}
            {carrier.authority_numbers?.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-sm text-foreground">Authority Numbers</span>
                </div>
                <div className="space-y-0">
                  {carrier.authority_numbers.map(auth => (
                    <div key={auth.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{auth.type?.replace(/_/g, ' ')}</p>
                        <p className="font-mono text-xs font-medium text-foreground">{auth.number}</p>
                      </div>
                      {auth.verified
                        ? <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">Verified</span>
                        : <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">Unverified</span>
                      }
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment & preferences (compact) */}
            {(carrier.equipment_types?.length > 0 || carrier.regions_served?.length > 0) && (
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <span className="font-semibold text-sm text-foreground">Equipment & Preferences</span>
                {carrier.equipment_types?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {carrier.equipment_types.map(t => (
                      <span key={t} className="px-2.5 py-1 rounded-lg bg-muted text-xs font-medium text-foreground capitalize">{t}</span>
                    ))}
                  </div>
                )}
                {carrier.regions_served?.length > 0 && (
                  <p className="text-xs text-muted-foreground">{carrier.regions_served.join(', ')}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Load history (full width) ── */}
        {ezData?.history?.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-sm text-foreground">Load history</span>
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full tabular-nums">
                {ezData.history.length}
              </span>
            </div>
            <div className="overflow-x-auto">
              {(() => {
                const HIDDEN_COLS = new Set(['carrier_id', 'dispatch_id']);
                const DATE_COLS   = new Set(['pickup_promised', 'pickup_actual', 'delivery_promised', 'delivery_actual']);
                const fmtDate = v => {
                  if (!v) return '--';
                  const d = new Date(v);
                  return isNaN(d) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                };
                const cols = Object.keys(ezData.history[0]).filter(c => !HIDDEN_COLS.has(c));
                return (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        {cols.map(col => (
                          <th key={col} className="text-left text-muted-foreground font-medium pb-2 pr-4 capitalize whitespace-nowrap">
                            {col.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ezData.history.map((row, i) => (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                          {cols.map(col => {
                            const val = row[col];
                            if (col === 'load_id') {
                              return (
                                <td key={col} className="py-2 pr-4 whitespace-nowrap">
                                  <Link
                                    to={`/admin/loads/${val}`}
                                    className="text-blue-500 hover:text-blue-400 font-mono underline underline-offset-2 transition-colors"
                                  >
                                    {val ? String(val).slice(-14) : '--'}
                                  </Link>
                                </td>
                              );
                            }
                            if (DATE_COLS.has(col)) {
                              return <td key={col} className="py-2 pr-4 text-foreground whitespace-nowrap">{fmtDate(val)}</td>;
                            }
                            return <td key={col} className="py-2 pr-4 text-foreground font-mono whitespace-nowrap">{String(val ?? '--')}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── Fraud signals ── */}
        {ezData?.fraud_signals?.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="font-semibold text-sm text-foreground">Fraud Signals</span>
            </div>
            <div className="space-y-2">
              {ezData.fraud_signals.map((sig, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    {typeof sig === 'string'
                      ? <p className="text-xs text-red-600 dark:text-red-400">{sig}</p>
                      : Object.entries(sig).map(([k, v]) => (
                          <p key={k} className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground capitalize">{k.replace(/_/g, ' ')}:</span> {String(v)}
                          </p>
                        ))
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onOpenChange={open => { if (!open) setEditOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Edit Carrier</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-5 pt-2">
            <CarrierForm form={form} setForm={setForm} />
            <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
